import { useCallback, useState } from 'react'
import {
  ApiErrorResponse,
  getAppClient,
  handleError,
} from '../../../utils/network_util'
import User from '../../../model/User'
import { useSnackBar } from '../../../providers/snack_bar_provider'

type Handler = {
  load: (id: string, cursor?: number) => Promise<void>
  block: (id: string, set: boolean) => Promise<void>
}

const useBlockUsers = (
  getLimit = 20
): [Array<User>, number, number, boolean, Handler] => {
  const apiClient = getAppClient()

  const [isLoading, setIsLoading] = useState(false)
  const [totalSize, setTotalSize] = useState(0)
  const [cursor, setCursor] = useState(0)
  const [blocks, setBlocks] = useState<Array<User>>([])
  const { showSnackBar } = useSnackBar()

  const load = useCallback(
    async (id: string, cursor = 0) => {
      if (cursor < 0 || (totalSize && cursor >= totalSize)) {
        return
      }

      setIsLoading(true)
      await apiClient.users
        .blocksDetail(id, { cursor: cursor, limit: getLimit })
        .then((res) => {
          const result = res.data
          if (totalSize !== result.total) {
            setTotalSize(result.total)
          }
          if (result.data) {
            setCursor(cursor)
            setBlocks(result.data)
          }
        })
        .catch((e: ApiErrorResponse) => {
          handleError(e, showSnackBar)
          return
        })
        .finally(() => {
          setIsLoading(false)
        })
    },
    [getLimit]
  )

  const block = useCallback(
    async (id: string, set: boolean) => {
      await apiClient.users
        .blockCreate({
          id: id,
          set: set,
        })
        .then(() => {
          if (set) {
            showSnackBar(['ブロックしました。'], 'success')
          } else {
            const filterBlocks = blocks.filter((user) => user.id !== id)
            setBlocks(filterBlocks)
            setTotalSize(prev => prev - 1)
            showSnackBar(['ブロック解除しました。'], 'success')
          }
        })
        .catch((e: ApiErrorResponse) => {
          handleError(e, showSnackBar)
          return
        })
    },
    [blocks, load, cursor]
  )

  return [blocks, totalSize, cursor, isLoading, { load: load, block: block }]
}

export default useBlockUsers
